<template>
  <div class="space-y-4">
    <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg drop-shadow-md"
    >
      <div class="w-full sm:flex-1 sm:mr-4">
        <input
            v-model="searchQuery"
            type="text"
            :placeholder="__('Search candidates')"
            class="w-full bg-white rounded-lg border border-gray-300 px-4 py-2 focus:ring-primary focus:border-primary"
            @input="handleSearchInput"
        />
      </div>

      <button
          @click="showFilters = !showFilters"
          class="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 text-primary whitespace-nowrap"
      >
        <Icon name="filter_alt" class="w-5 h-5 text-xl flex-shrink-0" is-filled />
        <span>{{ __('Filter Candidates') }}</span>
      </button>
    </div>
    <Transition name="slide-down">
      <div
          v-if="showFilters"
          class="bg-white rounded-lg drop-shadow-md p-6 space-y-4 relative"
          style="overflow: visible !important;"
      >
        <div
            class="relative grid grid-cols-1 sm:grid-cols-3 items-center gap-6"
            style="overflow: visible !important;"
        >
          <div class="relative sm:col-span-2" style="overflow: visible !important;">
            <InputSelect
                id="pointsFilter"
                v-model:value="filters.points"
                :label="__('Points')"
                :placeholder="__('Filter by points')"
                :options="pointRangeOptions"
            />
          </div>

          <div class="sm:col-span-1">
            <label class="block text-base font-medium text-zinc-800 mb-2">
              {{ __('Status') }}
            </label>
            <div class="flex flex-row items-center gap-6 justify-start sm:justify-start">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                    type="radio"
                    v-model="filters.status"
                    value="reviewed"
                    class="text-primary focus:ring-primary"
                />
                <span>{{ __('Reviewed') }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                    type="radio"
                    v-model="filters.status"
                    value="pending"
                    class="text-primary focus:ring-primary"
                />
                <span>{{ __('Pending Review') }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex justify-center sm:justify-end gap-3 pt-4">
          <button
              @click="resetFilters"
              class="px-4 py-2 flex items-center gap-2 text-primary hover:text-primary/80"
          >
            <Icon name="delete" class="w-4 h-4" />
            {{ __('Reset Filters') }}
          </button>
          <Button
              @click="applyFilters"
              class="bg-primary text-white"
          >
            {{ __('Apply Filters') }}
          </Button>
        </div>
      </div>
    </Transition>

    <div
        class="w-full overflow-x-auto overflow-y-hidden rounded-lg drop-shadow-md scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
        style="scrollbar-width: thin; scrollbar-color: #d4d4d8 #f5f5f5;"
    >
      <div
          class="min-w-[1000px] grid grid-cols-[200px,1fr,1fr,1fr,100px] bg-white p-4 text-sm font-medium text-zinc-700 text-center uppercase relative"
      >
        <div class="text-left">{{ __('Name') }}</div>
        <div>{{ __('Status') }}</div>
        <div>{{ __('Submission Date') }}</div>
        <div>{{ __('Points') }}</div>
        <div>{{ __('Actions') }}</div>
      </div>

      <div class="bg-white">
        <EmptyState
            v-if="!filteredSubmissions.length"
            :title="__('No Completions Yet!')"
            :subtitle="__(`Don't worry, candidates will start completing your challenge soon!`)"
            :button-text="__('Create Challenge')"
            button-route="redesign.company.challenges.create"
        />

        <div class="min-w-[1000px] bg-white divide-y divide-gray-100">
          <div
              v-for="(submission, index) in filteredSubmissions"
              :key="submission.id"
              class="grid grid-cols-[200px,1fr,1fr,1fr,100px] gap-4 p-4 items-center transition-all duration-200 relative"
              :class="{
                'hover:bg-gray-50': !isSelected(submission.id),
                'bg-body-color': isSelected(submission.id),
                'hover:bg-gray-200': isSelected(submission.id)
              }"
          >
            <div class="flex items-center gap-4 text-left">
              <input
                  type="checkbox"
                  :checked="isSelected(submission.id)"
                  @change="toggleSelection(submission.id)"
                  class="size-5 rounded-md border-gray-300 text-primary focus:shadow-none focus:outline-none focus:ring-0 focus:ring-offset-0"
              />
              <img
                  class="h-11 w-11 rounded-full ring-2 ring-white"
                  :src="submission.user.profile_photo"
                  :alt="submission.user.full_name"
              />
              <div class="min-w-0">
                <p class="text-sm font-semibold text-zinc-800 truncate">
                  {{ submission.user.full_name }}
                </p>
              </div>
            </div>

            <div class="flex text-center justify-center">
          <span
              :class="[
                'px-2.5 py-1 text-base rounded-md font-medium',
                submission.is_reviewed
                  ? 'bg-success/10 text-success'
                  : 'bg-error/10 text-error'
              ]"
          >
            {{ submission.is_reviewed ? __('Reviewed') : __('Pending Review') }}
          </span>
            </div>

            <div class="text-base text-zinc-600 text-center">
              {{ formattedSubmissionDate(submission.submitted_at) }}
            </div>

            <div class="text-sm font-medium text-center">
          <span class="text-base text-zinc-600">
            {{ submission.is_reviewed ? submission.points : 'N/A' }}
          </span>
            </div>

            <div class="flex items-center justify-center relative">
              <button @click="reviewSubmission(submission)">
                <Icon name="contract_edit" class="w-5 h-5 text-xl"/>
              </button>

              <ChallengeSubmissionActionsPopup
                  :submissionId="submission.id"
                  :pipelineOptions="pipelineOptions"
                  :jobOptions="jobOptions"
                  :isOpen="openActionsMenuId === submission.id"
                  :isLoadingPipeline="isLoadingPipeline"
                  :isLoadingJob="isLoadingJob"
                  :positionFromEnd="filteredSubmissions.length - index"
                  @toggle="toggleActionsMenu(submission.id)"
                  @reviewSubmission="reviewSubmission(submission)"
                  @addToPipeline="handleAddToPipeline"
                  @inviteToJob="handleInviteToJob"
                  @mobileAction="handleMobileAction"
              />
            </div>
          </div>

          <div
              v-if="hasMore && !isLoading && filteredSubmissions.length >= perPage"
              class="p-4 text-center"
          >
            <button
                @click="loadMore"
                class="hover-lift-no-shadow font-medium text-primary flex items-center justify-center gap-2 mx-auto"
                :disabled="isLoadingMore"
            >
              <span>{{ __('Show More') }}</span>
              <Icon
                  v-if="isLoadingMore"
                  name="progress_activity"
                  class="animate-spin text-lg"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <Transition class="bg-white rounded-lg drop-shadow-md">
      <div
          v-if="selectedSubmissions.length > 0"
          class="w-full bg-white py-5"
      >
        <transition
            v-if="isMobile && (activePopup === 'bulkPipeline' || mobileActionType)"
            enter-active-class="transition-all duration-150 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-100 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
          <div ref="mobileMenuContainer" class="pb-4 mb-4">
            <div class="max-w-7xl mx-auto px-4">
              <div v-if="mobileActionType" class="flex items-center justify-between mb-3">
                <button
                    @click="mobileActionType = null; mobileActionSubmissionId = null"
                    class="flex items-center gap-2 text-primary"
                >
                  <Icon name="arrow_back" class="w-5 h-5" />
                  <span>{{ __('Back') }}</span>
                </button>
                <span class="text-sm text-gray-600">
              {{ mobileActionType === 'pipeline' ? __('Select Pipeline') : __('Select Job') }}
            </span>
              </div>

              <div class="h-40 overflow-y-auto overscroll-contain scrollbar-thin border-b bg-white">
                <div
                    v-if="isLoadingPipeline || isLoadingJob"
                    class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white bg-opacity-95"
                >
                  <div class="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary"></div>
                </div>

                <ul v-if="(!mobileActionType || mobileActionType === 'pipeline') && pipelineOptions.length"
                    class="flex flex-col text-sm text-gray-700">
                  <li v-for="option in pipelineOptions" :key="option.id">
                    <button
                        @click.stop="handleMobileOptionSelect('pipeline', option)"
                        class="block w-full cursor-pointer rounded px-3.5 py-2 text-left transition-colors duration-150 hover:bg-gray-100"
                    >
                  <span class="overflow-hidden text-ellipsis whitespace-nowrap text-left text-base font-medium">
                    {{ option.name }}
                  </span>
                    </button>
                  </li>
                </ul>

                <ul v-if="mobileActionType === 'job' && jobOptions.length"
                    class="flex flex-col text-sm text-gray-700">
                  <li v-for="option in jobOptions" :key="option.id">
                    <button
                        @click.stop="handleMobileOptionSelect('job', option)"
                        class="block w-full cursor-pointer rounded px-3.5 py-2 text-left transition-colors duration-150 hover:bg-gray-100"
                    >
                  <span class="overflow-hidden text-ellipsis whitespace-nowrap text-left text-base font-medium">
                    {{ option.name }}
                  </span>
                    </button>
                  </li>
                </ul>

                <div v-if="(mobileActionType === 'pipeline' && !pipelineOptions.length) ||
                       (mobileActionType === 'job' && !jobOptions.length)"
                     class="flex h-full items-center justify-center p-4 text-center text-gray-500">
              <span>
                {{ mobileActionType === 'pipeline' ? __('No pipelines available') : __('No jobs available') }}
              </span>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <div class="max-w-7xl mx-auto flex items-center justify-between gap-2 px-4 sm:px-10">
          <div class="flex items-center gap-2 sm:gap-4 justify-center">
            <span class="text-xs sm:text-sm font-medium text-zinc-700 whitespace-nowrap">
              {{ selectedSubmissions.length }} {{ __('Selected') }}
            </span>

            <Button
                variant="outline-no-hover"
                class="hover-lift border-none flex items-center gap-2 text-xs sm:text-base"
                @click="toggleSelectAll"
            >
              <Icon name="fact_check" class="w-3 h-3 sm:w-4 sm:h-4 text-primary" is-filled/>
              <span class="text-primary whitespace-nowrap inline">
                {{ selectedSubmissions.length === filteredSubmissions.length ? __('Deselect All') : __('Select All') }}
              </span>
            </Button>
          </div>

          <div class="flex items-center">
            <Button
                v-if="isMobile"
                @click.stop="handlePopupToggle('bulkPipeline')"
                class="bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-primary/90 whitespace-nowrap"
            >
              <span>{{ __('Add to Pipeline') }}</span>
            </Button>
            <AddToPopup
                v-else
                :options="pipelineOptions"
                :is-loading="isLoadingPipeline"
                :show-icon="false"
                :is-open="activePopup === 'bulkPipeline'"
                popup-position="bottom"
                @select-option="handleBulkAddToPipeline"
                @toggle="handlePopupToggle('bulkPipeline')"
                :button-text="__('Add to Pipeline')"
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, onBeforeUnmount, provide, nextTick} from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { Button } from '@/components/block/button';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';
import EmptyState from "@/pages/candidate/challenges/components/EmptyState.vue";
import {InputSelect} from "@/components/form/input-select";
import ChallengeSubmissionActionsPopup from "@/pages/company/challenges/components/ChallengeSubmissionActionsPopup.vue";
import AddToPopup from "@/pages/company/challenges/components/AddToPopup.vue";

const props = defineProps({
  challengeId: {
    type: Number,
    required: true
  },
  submissions: {
    type: Array,
    default: () => []
  },
  initialHasMore: {
    type: Boolean,
    default: false
  },
  pipelineOptions: {
    type: Array,
    default: () => []
  },
  jobOptions: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits([
  'review-submission',
  'add-to-pipeline',
  'invite-to-job',
  'bulk-add-to-pipeline'
]);

const toast = useToast();

const submissions = ref(props.submissions);
const searchQuery = ref('');
const selectedSubmissions = ref([]);
const isLoading = ref(false);
const isLoadingJob = ref(false);
const isLoadingPipeline = ref(false);
const isLoadingMore = ref(false);
const showFilters = ref(false);
const currentPage = ref(1);

const hasMore = ref(props.initialHasMore);
const perPage = 5;
const openActionsMenuId = ref(null);
const activePopup = ref(null);

const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640)
const mobileActionSubmissionId = ref(null);
const mobileActionType = ref(null);
const mobileMenuContainer = ref(null);

const filters = ref({
  points: '',
  status: '',
});

const pointRangeOptions = computed(() => [
  { id: 'under100', name: __('< 100') },
  { id: '100-200', name: __('100 - 200') },
  { id: '200-300', name: __('200 - 300') },
  { id: 'above300', name: __('> 300') }
]);

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

const handleMobileAction = (submissionId, actionType) => {
  if (isMobile.value) {
    mobileActionSubmissionId.value = submissionId;
    mobileActionType.value = actionType;
    selectedSubmissions.value = [submissionId];
    openActionsMenuId.value = null;

    nextTick(() => {
      if (mobileMenuContainer.value) {
        mobileMenuContainer.value.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        })
      }
    })
  }
};

provide('mobileActionHandler', handleMobileAction);
provide('isMobile', isMobile);

const handleMobileOptionSelect = async (type, option) => {
  if (type === 'pipeline') {
    if (mobileActionSubmissionId.value) {
      await handleAddToPipeline({
        submissionId: mobileActionSubmissionId.value,
        pipelineId: option.id
      });
    } else {
      await handleBulkAddToPipeline(option);
    }
  } else if (type === 'job') {
    await handleInviteToJob({
      submissionId: mobileActionSubmissionId.value,
      jobId: option.id
    });
  }

  mobileActionType.value = null;
  mobileActionSubmissionId.value = null;
  if (!activePopup.value) {
    selectedSubmissions.value = [];
  }
};
const toggleActionsMenu = (submissionId) => {
  openActionsMenuId.value = openActionsMenuId.value === submissionId ? null : submissionId;
};

const filteredSubmissions = computed(() => submissions.value);

const applyFilters = async () => {
  showFilters.value = false;
  await loadSubmissions();
};

const resetFilters = async () => {
  filters.value = {
    points: '',
    status: ''
  };
  searchQuery.value = '';
  await loadSubmissions();
};

const isSelected = (submissionId) => {
  return selectedSubmissions.value.includes(submissionId);
};

const toggleSelection = (submissionId) => {
  const index = selectedSubmissions.value.indexOf(submissionId);
  if (index > -1) {
    selectedSubmissions.value.splice(index, 1);
  } else {
    selectedSubmissions.value.push(submissionId);
  }
};

const toggleSelectAll = () => {
  if (selectedSubmissions.value.length === filteredSubmissions.value.length) {
    selectedSubmissions.value = [];
  } else {
    selectedSubmissions.value = filteredSubmissions.value.map(s => s.id);
  }
};

const formattedSubmissionDate = (date) => {
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const reviewSubmission = (submission) => {
  openActionsMenuId.value = null;
  emit('review-submission', submission);
};

const handlePopupToggle = (popupName) => {
  if (activePopup.value === popupName) {
    activePopup.value = null;
  } else {
    activePopup.value = popupName;

    if (isMobile.value) {
      nextTick(() => {
        if (mobileMenuContainer.value) {
          mobileMenuContainer.value.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          });
        }
      })
    }
  }
};

const handleAddToPipeline = async (data) => {
  openActionsMenuId.value = null;
  isLoadingPipeline.value = true;

  try {
    const submission = submissions.value.find(s => s.id === data.submissionId);

    if (submission) {
      const response = await axios.post(route('redesign.company.challenges.add-to-pipeline'), {
        pipeline_id: data.pipelineId,
        user_id: submission.user.id,
      });

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(__('Failed to add to pipeline'));
    }
  } finally {
    isLoadingPipeline.value = false;
  }
};

const handleInviteToJob = async (data) => {
  openActionsMenuId.value = null;
  isLoadingJob.value = true;

  try {
    const submission = submissions.value.find(s => s.id === data.submissionId);
    if (submission) {
      const response = await axios.post(route('redesign.company.challenges.invite-to-job'), {
        job_id: data.jobId,
        user_id: submission.user.id,
      });

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    }
  } catch (error) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(__('Failed to send invite'));
    }
  } finally {
    isLoadingJob.value = false;
  }
};

const loadMore = async () => {
  isLoadingMore.value = true;
  try {
    const response = await axios.get(route('redesign.company.challenges.load-more-submissions'), {
      params: {
        challenge_id: props.challengeId,
        page: currentPage.value + 1,
        search: searchQuery.value,
        points: filters.value.points || null,
        status: filters.value.status || null,
      }
    });

    submissions.value.push(...response.data.data);
    currentPage.value++;
    hasMore.value = response.data.has_more;
  } catch (error) {
    toast.error(__('Failed to load more submissions'));
  } finally {
    isLoadingMore.value = false;
  }
};

let searchTimeout = null;
const handleSearchInput = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(async () => {
    await loadSubmissions();
  }, 300);
};

const loadSubmissions = async () => {
  isLoading.value = true;
  try {
    const response = await axios.get(route('redesign.company.challenges.load-more-submissions'), {
      params: {
        challenge_id: props.challengeId,
        search: searchQuery.value,
        page: 1,
        points: filters.value.points || null,
        status: filters.value.status || null,
      }
    });

    submissions.value = response.data.data;
    currentPage.value = 1;
    hasMore.value = response.data.has_more;
    selectedSubmissions.value = [];
  } catch (error) {
    toast.error(__('Failed to load submissions'));
  } finally {
    isLoading.value = false;
  }
};

const handleBulkAddToPipeline = async (pipeline) => {
  isLoadingPipeline.value = true;

  try {
    const selectedSubmissionData = submissions.value.filter(s =>
        selectedSubmissions.value.includes(s.id)
    );

    const userIds = selectedSubmissionData.map(submission => submission.user.id);

    const response = await axios.post(route('redesign.company.challenges.bulk-add-to-pipeline'), {
      pipeline_id: pipeline.id,
      user_ids: userIds,
    });

    if (response.data.success) {
      toast.success(response.data.message);

      if (response.data.data?.skipped > 0) {
        toast.error(
            __(`${response.data.data.skipped} users were already in the pipeline`)
        );
      }

      selectedSubmissions.value = [];
      activePopup.value = null;
    } else {
      toast.warning(response.data.message);
    }
  } catch (error) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error(__('Failed to add to pipeline'));
    }
  } finally {
    isLoadingPipeline.value = false;
  }
};
</script>
